import { containsPoint, borderPoint, radiusAt } from './nodes';
import {
  DEFAULT_LOOP_ANGLE, NEGATIVE, isSelfLoop, sameId, connectsPair,
} from '../model';

export const ARROW_LENGTH = 11;
export const ARROW_WIDTH = 10;
export const DOT_RADIUS = 5;
export const LOOP_RADIUS = 20;
/** Halo around a node where links attach, so markers sit clear of the outline. */
export const NODE_PAD = 1;
/** Sideways shift (px) when two links share a node pair, so both stay visible. */
export const PAIR_OFFSET = 7;

export const paddedSize = (size) => ({
  width: size.width + NODE_PAD * 2,
  height: size.height + NODE_PAD * 2,
});

const lerp = (a, b, t) => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const fmt = (value) => Math.round(value * 100) / 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const unit = (v) => {
  const len = Math.hypot(v.x, v.y) || 1;
  return { x: v.x / len, y: v.y / len };
};

/** 90° counter-clockwise in screen coordinates (y down). */
const perpCounterClockwise = (dir) => ({ x: dir.y, y: -dir.x });

const quadPoint = (p0, v, p2, t) => {
  const s = 1 - t;
  return {
    x: s * s * p0.x + 2 * s * t * v.x + t * t * p2.x,
    y: s * s * p0.y + 2 * s * t * v.y + t * t * p2.y,
  };
};

const quadTangent = (p0, v, p2, t) => ({
  x: 2 * (1 - t) * (v.x - p0.x) + 2 * t * (p2.x - v.x),
  y: 2 * (1 - t) * (v.y - p0.y) + 2 * t * (p2.y - v.y),
});

/** Arrow head at `tip` pointing along `dir`, plus where the line should stop. */
const markerFor = (sign, tip, dir) => {
  if (sign === NEGATIVE) {
    return {
      type: 'circle',
      cx: fmt(tip.x - dir.x * DOT_RADIUS),
      cy: fmt(tip.y - dir.y * DOT_RADIUS),
      r: DOT_RADIUS,
      lineEnd: { x: fmt(tip.x - dir.x * DOT_RADIUS * 2), y: fmt(tip.y - dir.y * DOT_RADIUS * 2) },
    };
  }
  const base = { x: tip.x - dir.x * ARROW_LENGTH, y: tip.y - dir.y * ARROW_LENGTH };
  const nx = (-dir.y * ARROW_WIDTH) / 2;
  const ny = (dir.x * ARROW_WIDTH) / 2;
  const corners = [tip, { x: base.x + nx, y: base.y + ny }, { x: base.x - nx, y: base.y - ny }];
  return {
    type: 'triangle',
    points: corners.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(' '),
    lineEnd: { x: fmt(base.x), y: fmt(base.y) },
  };
};

/**
 * Where a straight line from `insidePt` toward `outsidePt` leaves `node`.
 * If `insidePt` is already outside (large offset), attach at the border in that direction.
 */
const exitPoint = (node, size, insidePt, outsidePt) => {
  if (!containsPoint(node, size, insidePt.x, insidePt.y)) {
    return borderPoint(
      node,
      size,
      Math.atan2(insidePt.y - node.y, insidePt.x - node.x),
    );
  }
  let inside = 0;
  let outside = 1;
  for (let i = 0; i < 24; i += 1) {
    const mid = (inside + outside) / 2;
    const p = lerp(insidePt, outsidePt, mid);
    if (containsPoint(node, size, p.x, p.y)) inside = mid;
    else outside = mid;
  }
  return lerp(insidePt, outsidePt, outside);
};

/** Bisect the curve parameter between a point inside `node` and one outside it. */
const findBorderT = (p0, v, p2, node, size, tInside, tOutside) => {
  let inside = tInside;
  let outside = tOutside;
  for (let i = 0; i < 24; i += 1) {
    const mid = (inside + outside) / 2;
    const p = quadPoint(p0, v, p2, mid);
    if (containsPoint(node, size, p.x, p.y)) inside = mid; else outside = mid;
  }
  return outside;
};

const shiftedChord = (from, to, offset) => {
  const dir = unit({ x: to.x - from.x, y: to.y - from.y });
  const shift = perpCounterClockwise(dir);
  return {
    dir,
    shift,
    p0: { x: from.x + shift.x * offset, y: from.y + shift.y * offset },
    p2: { x: to.x + shift.x * offset, y: to.y + shift.y * offset },
  };
};

/** Pair gap from settings. Stored `edge.offset` is ignored so old files follow the Gap control. */
export const offsetForPair = (edge, edges, amount = PAIR_OFFSET) => {
  if (isSelfLoop(edge)) return 0;
  const paired = edges.some((other) => (
    !sameId(other.id, edge.id) && !isSelfLoop(other) && connectsPair(other, edge.from, edge.to)
  ));
  return paired ? amount : 0;
};

/**
 * Stored `loopAngle` for a self effect whose centre should sit toward `point`.
 * 0 = east, increasing toward north (as in the editor slider).
 */
export const loopAngleFromDrag = (node, point) => {
  const phi = Math.atan2(point.y - node.y, point.x - node.x);
  const angle = -phi;
  const twoPi = Math.PI * 2;
  return ((angle % twoPi) + twoPi) % twoPi;
};

/**
 * Signed curvature (-1..1) that places the curve's midpoint at `point`.
 * Positive bows counter-clockwise of from→to.
 */
export const roundnessFromDrag = (from, to, point, offset = 0) => {
  const { shift, p0, p2 } = shiftedChord(from, to, offset);
  const mid = lerp(p0, p2, 0.5);
  const length = Math.hypot(p2.x - p0.x, p2.y - p0.y) || 1;
  const d = (point.x - mid.x) * shift.x + (point.y - mid.y) * shift.y;
  return clamp(d / (0.5 * length), -1, 1);
};

const curveAmount = (roundness) => {
  if (typeof roundness !== 'number' || Math.abs(roundness) < 1e-6) return 0;
  return clamp(roundness, -1, 1);
};

const loopGeometry = (edge, node, size) => {
  const loopAngle = typeof edge.loopAngle === 'number' ? edge.loopAngle : DEFAULT_LOOP_ANGLE;
  // The loop centre sits on the border, `loopAngle` measured counter-clockwise from east
  // (0 = east, pi/2 = north), as in files written by earlier versions.
  const phi = -loopAngle;
  const dist = radiusAt(node, size, phi);
  const c = { x: node.x + Math.cos(phi) * dist, y: node.y + Math.sin(phi) * dist };
  const r = LOOP_RADIUS;
  const at = (theta) => ({ x: c.x + r * Math.cos(theta), y: c.y + r * Math.sin(theta) });

  // Walk clockwise from the outermost point until the loop re-enters the node.
  let outside = phi;
  let inside = phi + Math.PI;
  for (let i = 0; i < 24; i += 1) {
    const mid = (inside + outside) / 2;
    const p = at(mid);
    if (containsPoint(node, size, p.x, p.y)) inside = mid; else outside = mid;
  }
  const tip = at(outside);
  const dir = { x: -Math.sin(outside), y: Math.cos(outside) };
  return {
    path: `M ${fmt(c.x - r)} ${fmt(c.y)} A ${r} ${r} 0 1 0 ${fmt(c.x + r)} ${fmt(c.y)} A ${r} ${r} 0 1 0 ${fmt(c.x - r)} ${fmt(c.y)}`,
    marker: markerFor(edge.sign, tip, dir),
    loop: { cx: fmt(c.x), cy: fmt(c.y), r },
    midpoint: at(phi),
  };
};

const straightGeometry = (edge, from, fromSize, to, toSize, offset) => {
  const { dir, p0, p2 } = shiftedChord(from, to, offset);
  const start = offset === 0
    ? borderPoint(from, fromSize, Math.atan2(dir.y, dir.x))
    : exitPoint(from, fromSize, p0, p2);
  const tip = offset === 0
    ? borderPoint(to, toSize, Math.atan2(-dir.y, -dir.x))
    : exitPoint(to, toSize, p2, p0);
  const marker = markerFor(edge.sign, tip, dir);
  return {
    path: `M ${fmt(start.x)} ${fmt(start.y)} L ${marker.lineEnd.x} ${marker.lineEnd.y}`,
    marker,
    midpoint: lerp(start, tip, 0.5),
  };
};

const curvedGeometry = (edge, from, fromSize, to, toSize, offset, roundness) => {
  const { shift, p0, p2 } = shiftedChord(from, to, offset);
  const length = Math.hypot(p2.x - p0.x, p2.y - p0.y) || 1;
  const d = roundness * length * 0.5;
  const mid = lerp(p0, p2, 0.5);
  const via = { x: mid.x + shift.x * 2 * d, y: mid.y + shift.y * 2 * d };
  const t0 = findBorderT(p0, via, p2, from, fromSize, 0, 0.5);
  const t1 = findBorderT(p0, via, p2, to, toSize, 1, 0.5);
  const start = quadPoint(p0, via, p2, t0);
  const tip = quadPoint(p0, via, p2, t1);
  const dir = unit(quadTangent(p0, via, p2, t1));
  const marker = markerFor(edge.sign, tip, dir);
  const s = (t1 - t0) / (1 - t0);
  const control = lerp(start, lerp(via, p2, t0), s);
  return {
    path: `M ${fmt(start.x)} ${fmt(start.y)} Q ${fmt(control.x)} ${fmt(control.y)} ${marker.lineEnd.x} ${marker.lineEnd.y}`,
    marker,
    midpoint: quadPoint(p0, via, p2, (t0 + t1) / 2),
  };
};

/**
 * Everything needed to draw one edge: an SVG path for the line, an arrow-head description and
 * a point near the middle of the edge. `offset` shifts a pair sideways; `roundness` bows the
 * line (positive = counter-clockwise). Nodes are `{ x, y, shape }` and sizes `{ width, height }`.
 */
export const edgeGeometry = ({
  edge, from, fromSize, to, toSize, offset = 0,
}) => {
  const fromPad = paddedSize(fromSize);
  const toPad = paddedSize(toSize);
  if (isSelfLoop(edge)) return loopGeometry(edge, from, fromPad);

  const roundness = curveAmount(edge.roundness);
  if (roundness === 0) return straightGeometry(edge, from, fromPad, to, toPad, offset);
  return curvedGeometry(edge, from, fromPad, to, toPad, offset, roundness);
};

/** Geometry of the temporary line shown while dragging a new link from `from` to a point. */
export const connectionGeometry = ({
  from, fromSize, to, sign,
}) => {
  const fromPad = paddedSize(fromSize);
  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const start = borderPoint(from, fromPad, angle);
  const dir = { x: Math.cos(angle), y: Math.sin(angle) };
  const marker = markerFor(sign, to, dir);
  return { path: `M ${fmt(start.x)} ${fmt(start.y)} L ${marker.lineEnd.x} ${marker.lineEnd.y}`, marker };
};
